import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from "react";
import { api, apiFetch } from "../services/api";
import AddKnowledgeAnchorPanel from "./AddKnowledgeAnchorPanel";
import MultiFileUpload from "./MultiFileUpload";
import S3ConnectorImport from "./S3ConnectorImport";
import TextareaWithAutocomplete from "./TextareaWithAutocomplete";
import LoadingButton from "./common/LoadingButton";
import { useDeviceDetection } from "../hooks/useDeviceDetection";
import { useAiJobState } from "../hooks/useAiJobState";
import {
  beginOptimisticEnrichmentQueue,
  ensureEnrichmentQueueVisibleFromUpload,
  finalizeOptimisticEnrichmentQueue,
  finalizeOptimisticEnrichmentQueueOnTimeout,
  isEnrichmentQueueTimeoutError,
  rollbackOptimisticEnrichmentQueue,
} from "../utils/enrichmentClientCache";
import { createDevConsole } from "../utils/devLogger";
import {
  isLlmQuotaExhausted,
  LLM_QUOTA_EXHAUSTED_MESSAGE,
} from "../utils/llmQuota";
import {
  addKnowledgeQueueLabel,
  addKnowledgeSuccessMessage,
  queueAddKnowledgeJob,
} from "../utils/addKnowledgeQueue";
import {
  clearKnowledgePrefill,
  peekKnowledgePrefill,
  stashKnowledgePrefill,
} from "../utils/knowledgePrefill";
import { emitGuideEvent } from "../v2/guide/guideEvents";
import { useOptionalProductGuide } from "../v2/guide/GuideContext";
import {
  expectedWizardStepForChapter,
  isGuideFocusChapter,
  isWizardStepBeforeGuide,
  wizardStepIndex,
} from "../v2/guide/guideProgress";
import { readGuideState } from "../v2/guide/guideStorage";
import {
  buildGuideWizardPrefill,
  ensureGuideWizardPrefill,
  guideNeedsAddKnowledgeRoute,
} from "../v2/guide/guideWizardResume";
import { revealGuideAttentionTarget } from "../v2/utils/scrollShell";
import {
  AddKnowledgeWizardBackButton,
  ExtractModeOption,
} from "./addKnowledgeWizardUi";

const console = createDevConsole();
const debugLog = (...args) => console.log(...args);
const debugWarn = (...args) => console.warn(...args);
const debugError = (...args) => console.error(...args);

/** Primary ways to bring content into the knowledge base (replaces legacy 3-tab strip). */
const ADD_KNOWLEDGE_METHODS = [
  {
    id: "text",
    title: "Paste or type",
    hint: "Notes or any text",
    emoji: "✏️",
    badge: "Fastest",
    embed: {
      gradient: "bg-gradient-to-br from-sky-50 via-white to-indigo-50",
      border: "border-sky-200/70",
      ringHover: "hover:ring-2 hover:ring-sky-300/50",
      iconShell: "bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-md shadow-sky-500/25",
    },
  },
  {
    id: "file",
    title: "Upload files",
    hint: "PDF, Word, specs, blueprints",
    emoji: "📚",
    badge: null,
    embed: {
      gradient: "bg-gradient-to-br from-violet-50 via-white to-fuchsia-50",
      border: "border-violet-200/70",
      ringHover: "hover:ring-2 hover:ring-violet-300/50",
      iconShell: "bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-md shadow-violet-500/25",
    },
  },
  {
    id: "s3",
    title: "Amazon S3",
    hint: "Import from a connected bucket",
    emoji: "☁️",
    badge: null,
    embed: {
      gradient: "bg-gradient-to-br from-amber-50 via-white to-teal-50",
      border: "border-amber-200/70",
      ringHover: "hover:ring-2 hover:ring-amber-300/50",
      iconShell: "bg-gradient-to-br from-amber-500 to-teal-600 text-white shadow-md shadow-amber-500/20",
    },
  },
];

const KNOWLEDGE_SCOPE_OPTIONS = [
  {
    id: "content",
    title: "Something new — not on the map yet",
    hint: "We’ll identify the entities and relationships for your review.",
    emoji: "\u2728",
    cardClass:
      "group relative flex w-full flex-col rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-4 text-left shadow-sm transition duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md hover:ring-2 hover:ring-emerald-300/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
    iconClass:
      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-500/25",
  },
  {
    id: "entity",
    title: "An existing architecture entity",
    hint: "Application, organisation, capability, data, technology…",
    emoji: "\uD83D\uDDFA\uFE0F",
    cardClass:
      "group relative flex w-full flex-col rounded-2xl border border-rose-200/70 bg-gradient-to-br from-rose-50 via-white to-orange-50 p-4 text-left shadow-sm transition duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md hover:ring-2 hover:ring-rose-300/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
    iconClass:
      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg bg-gradient-to-br from-rose-500 to-orange-600 text-white shadow-md shadow-rose-500/25",
  },
  {
    id: "knowledge_surface",
    title: "General knowledge — no specific entity",
    hint: "Principles, patterns, SOPs, standards, articles.",
    emoji: "\uD83D\uDCDA",
    cardClass:
      "group relative flex w-full flex-col rounded-2xl border border-teal-200/70 bg-gradient-to-br from-teal-50 via-white to-cyan-50 p-4 text-left shadow-sm transition duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md hover:ring-2 hover:ring-teal-300/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
    iconClass:
      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-md shadow-teal-500/25",
  },
];

function AddKnowledgeScopePanel({ onSelect, onBack }) {
  const { isMobile } = useDeviceDetection();
  return (
    <div className={isMobile ? "space-y-3" : "space-y-3"}>
      {/* Step label, question and hint share one line instead of three. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-left">
        {onBack ? (
          <AddKnowledgeWizardBackButton onClick={onBack}>
            Change knowledge type
          </AddKnowledgeWizardBackButton>
        ) : null}
        <p className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Step 1 · Target
        </p>
        <p className="text-sm font-semibold tracking-tight text-slate-900">
          What is this knowledge about?
        </p>
      </div>

      <div
        className={
          isMobile
            ? "grid grid-cols-1 gap-2.5"
            : "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
        }
        role="group"
        aria-label="Knowledge target"
      >
        {KNOWLEDGE_SCOPE_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onSelect?.(option.id)}
            className={`${option.cardClass}${
              isMobile ? " !min-h-0 !p-3.5" : ""
            }`}
          >
            <div className={`flex items-start ${isMobile ? "gap-3" : "gap-3"}`}>
              <div
                className={`${option.iconClass}${
                  isMobile ? " !h-10 !w-10 !rounded-xl !text-lg" : ""
                }`}
                aria-hidden
              >
                {option.emoji}
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                <span className="text-sm font-semibold tracking-tight text-slate-900">
                  {option.title}
                </span>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">
                  {option.hint}
                </p>
              </div>
            </div>
            <div
              className={`mt-auto flex items-center justify-end gap-1.5 text-xs font-medium text-blue-700 opacity-90 transition group-hover:opacity-100 ${
                isMobile ? "pt-3" : "pt-2.5"
              }`}
            >
              <span>Continue</span>
              <svg
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function systemInputMessageTone(message) {
  const m = String(message || "");
  if (m.includes("No meaningful") || m.includes("No entities")) return "warn";
  if (m.includes("Error") || m.includes("❌")) return "error";
  if (
    m.includes("Success") ||
    m.includes("✅") ||
    m.includes("successfully")
  ) {
    return "success";
  }
  return "info";
}

function systemInputMessageBannerClass(message) {
  const tone = systemInputMessageTone(message);
  const base = "mb-4 p-3 rounded-lg border shadow-sm";
  if (tone === "warn") {
    return `${base} bg-amber-50 text-amber-950 border-amber-200`;
  }
  if (tone === "error") {
    return `${base} bg-red-50 text-red-900 border-red-200`;
  }
  if (tone === "success") {
    return `${base} bg-emerald-50 text-emerald-900 border-emerald-200`;
  }
  return `${base} bg-blue-50 text-blue-950 border-blue-200`;
}

function bootFromKnowledgePrefill(prefill) {
  const empty = {
    embedFlowStep: "scope",
    knowledgeScopeType: null,
    knowledgeAnchor: null,
    guideCreateName: "",
    guideStartCreate: false,
    pendingGuidePaste: "",
    sourceHint: "",
    initialTab: "text",
    hadPrefill: false,
  };
  if (!prefill) return empty;
  const scopeType =
    prefill.scopeType === "knowledge_surface"
      ? "knowledge_surface"
      : prefill.scopeType === "content"
        ? "content"
        : "entity";
  const allowedSteps = ["scope", "anchor", "pick", "source"];
  const wantedStep = String(prefill.startStep || "").trim();
  const startStep = allowedSteps.includes(wantedStep) ? wantedStep : "";
  const paste = prefill.pasteText ? String(prefill.pasteText) : "";
  const initialTab = ["text", "file", "s3"].includes(prefill.inputTab)
    ? prefill.inputTab
    : "text";

  if (prefill.id) {
    return {
      embedFlowStep: startStep || "pick",
      knowledgeScopeType: scopeType,
      knowledgeAnchor: {
        id: String(prefill.id),
        name: prefill.name || "",
        scopeType,
        extractMode:
          prefill.extractMode ||
          (prefill.extractEntities ? "force_on" : "auto"),
      },
      guideCreateName: "",
      guideStartCreate: false,
      pendingGuidePaste: paste,
      sourceHint: prefill.nextStepHint || "",
      initialTab,
      hadPrefill: true,
    };
  }
  if (scopeType === "content") {
    return {
      embedFlowStep: startStep || "source",
      knowledgeScopeType: "content",
      knowledgeAnchor: {
        id: "content",
        name: "New — identified from your content",
        scopeType: "content",
        extractMode: "force_on",
      },
      guideCreateName: "",
      guideStartCreate: false,
      pendingGuidePaste: paste,
      sourceHint: prefill.nextStepHint || (paste ? "extract" : ""),
      initialTab,
      hadPrefill: true,
    };
  }
  if (prefill.intent === "create") {
    return {
      // Create flow has no entity yet — only scope or anchor are valid.
      embedFlowStep: startStep === "scope" ? "scope" : "anchor",
      knowledgeScopeType: scopeType,
      knowledgeAnchor: null,
      guideCreateName: String(
        prefill.suggestedName || prefill.name || ""
      ).trim(),
      guideStartCreate: true,
      pendingGuidePaste: paste,
      sourceHint: prefill.nextStepHint || "",
      initialTab,
      hadPrefill: true,
    };
  }
  return empty;
}

const SystemInput = ({
  onNavigateToGraph,
  onEntitiesProcessed,
  onNavigateToInbox,
}) => {
  const { isMobile } = useDeviceDetection();
  const { setAiProcessing } = useAiJobState();
  const productGuide = useOptionalProductGuide();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [trialStatus, setTrialStatus] = useState(null);

  // Simple loading state
  const [isProcessing, setIsProcessing] = useState(false);

  // Wizard flow states - simplified for queued processing
  const [wizardStep, setWizardStep] = useState("input"); // 'input', 'processing', 'queued', 'success'

  /**
   * Boot wizard from session prefill in useState (not an effect) so:
   * - first paint is already on the right step (no scope flash)
   * - React Strict Mode remount still peeks the same cached prefill
   * - guided-demo refresh restores entity + wizard step from guide state
   */
  const [prefillBoot] = useState(() => {
    const existing = peekKnowledgePrefill();
    if (existing) return bootFromKnowledgePrefill(existing);
    const resumed = ensureGuideWizardPrefill(readGuideState());
    return bootFromKnowledgePrefill(resumed);
  });

  const [activeTab, setActiveTab] = useState(prefillBoot.initialTab); // 'text', 'file', 's3'

  // Amazon S3 is desktop-only in the Add knowledge wizard.
  useEffect(() => {
    if (isMobile && activeTab === "s3") setActiveTab("text");
  }, [isMobile, activeTab]);

  /** v2 embedded: scope → anchor → pick method → paste/upload/S3 content. */
  const [embedFlowStep, setEmbedFlowStep] = useState(prefillBoot.embedFlowStep);
  const [knowledgeScopeType, setKnowledgeScopeType] = useState(
    prefillBoot.knowledgeScopeType
  );
  const [knowledgeAnchor, setKnowledgeAnchor] = useState(
    prefillBoot.knowledgeAnchor
  );
  const [guideCreateName, setGuideCreateName] = useState(
    prefillBoot.guideCreateName
  );
  const [guideStartCreate, setGuideStartCreate] = useState(
    prefillBoot.guideStartCreate
  );
  const [pendingGuidePaste, setPendingGuidePaste] = useState(
    prefillBoot.pendingGuidePaste
  );
  // Landing straight on the source step (Ask-home example / paste actions):
  // the prefilled text belongs in the box from first paint, not behind a
  // later "pick a method" click.
  const [description, setDescription] = useState(() =>
    prefillBoot.embedFlowStep === "source" ? prefillBoot.pendingGuidePaste : ""
  );
  const [sourceHint, setSourceHint] = useState(prefillBoot.sourceHint);
  const guideExtractCtaRef = useRef(null);
  const guideMethodCtaRef = useRef(null);

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileProcessingError, setFileProcessingError] = useState("");

  // Reset processing state on component mount
  useEffect(() => {
    setIsProcessing(false);
    setLoading(false);
  }, []);

  // Fetch trial status on component mount and when refreshed
  const fetchTrialStatus = async () => {
    try {
      debugLog("🔄 Fetching trial status...");
      const response = await apiFetch("/api/auth/trial-status", {
        method: "GET",
      });
      if (response && response.ok) {
        const data = await response.json();
        debugLog("✅ Trial status fetched:", data);
        setTrialStatus(data);
      } else {
        debugWarn("⚠️ Trial status response was not OK:", response?.status);
      }
    } catch (error) {
      debugError("❌ Error fetching trial status:", error);
      // Don't show error to user, just log it
    }
  };

  useEffect(() => {
    fetchTrialStatus();

    // Listen for direct status updates (dispatched when usage is tracked)
    const handleStatusUpdate = (event) => {
      if (event.detail) {
        setTrialStatus(event.detail);
      }
    };

    window.addEventListener("trialStatusUpdated", handleStatusUpdate);

    return () => {
      window.removeEventListener("trialStatusUpdated", handleStatusUpdate);
    };
  }, []);

  // Drop prefill after mount settles — except mid guided Add-knowledge,
  // where session prefill must survive refresh (entity + CPH notes).
  useEffect(() => {
    if (!prefillBoot.hadPrefill) return undefined;
    if (guideNeedsAddKnowledgeRoute(readGuideState())) return undefined;
    const t = window.setTimeout(() => clearKnowledgePrefill(), 0);
    return () => window.clearTimeout(t);
  }, [prefillBoot.hadPrefill]);

  useEffect(() => {
    const onSample = (ev) => {
      const text = String(ev?.detail?.text || "").trim();
      if (!text) return;
      setDescription(text);
      setPendingGuidePaste(text);
      setActiveTab("text");
      setSourceHint("extract");
      setMessage("Sample notes loaded — review, then Add knowledge.");
      if (knowledgeAnchor?.id) {
        setEmbedFlowStep("source");
        // Keep refresh resume warm: source step + notes + Extract cue.
        stashKnowledgePrefill({
          scopeType: "entity",
          id: knowledgeAnchor.id,
          name: knowledgeAnchor.name || "",
          suggestedType: "application",
          startStep: "source",
          pasteText: text,
          nextStepHint: "extract",
        });
      }
    };
    window.addEventListener("dp-guide-load-sample", onSample);
    return () => window.removeEventListener("dp-guide-load-sample", onSample);
  }, [knowledgeAnchor?.id, knowledgeAnchor?.name]);

  const handleKnowledgeAnchorChange = useCallback((next) => {
    if (!next?.id) {
      setKnowledgeAnchor(null);
      return;
    }
    const scopeType =
      next.scopeType === "knowledge_surface"
        ? "knowledge_surface"
        : next.scopeType === "content"
          ? "content"
          : "entity";
    setKnowledgeAnchor((prev) => ({
      ...next,
      scopeType,
      extractMode:
        scopeType === "entity"
          ? prev?.id === String(next.id) && prev?.extractMode
            ? prev.extractMode
            : next.extractMode || "auto"
          : "auto",
    }));
    emitGuideEvent("anchor-ready", {
      entityId: String(next.id),
      entityName: String(next.name || ""),
    });
  }, []);

  const handleExtractModeChange = useCallback((mode) => {
    setKnowledgeAnchor((prev) =>
      prev ? { ...prev, extractMode: mode || "auto" } : prev
    );
  }, []);

  // Debug wizard step changes
  useEffect(() => {
    debugLog("🔄 Wizard step changed to:", wizardStep);
  }, [wizardStep]);

  /** Keep guided tour + strip tips in sync with live wizard UI. */
  useEffect(() => {
    // After Extract is queued, stop claiming the source step — guide owns
    // processing / Open Inbox from here.
    if (
      wizardStep === "queued" ||
      wizardStep === "processing" ||
      wizardStep === "success" ||
      wizardStep === "error"
    ) {
      return;
    }
    const hasNotes = Boolean(
      String(description || "").trim() || String(pendingGuidePaste || "").trim()
    );
    emitGuideEvent("wizard-step", {
      step: embedFlowStep,
      hasAnchor: Boolean(knowledgeAnchor?.id),
      hasNotes,
    });
  }, [
    description,
    embedFlowStep,
    knowledgeAnchor?.id,
    pendingGuidePaste,
    wizardStep,
  ]);

  const guideChapterId = productGuide?.chapter?.id || "";
  const guideWizardLock = Boolean(
    productGuide?.active && isGuideFocusChapter(guideChapterId)
  );
  const guideMinWizardStep = guideWizardLock
    ? expectedWizardStepForChapter(guideChapterId)
    : null;
  const highlightExtractCta = Boolean(
    ((sourceHint === "extract" && description.trim()) ||
      (guideWizardLock && description.trim())) &&
      !loading &&
      embedFlowStep === "source"
  );
  const highlightMethodCta = Boolean(
    guideWizardLock && embedFlowStep === "pick"
  );

  // Guided demo: blinking CTAs must not sit below a scrollbar.
  // Double rAF waits for the notes textarea to expand before measuring.
  useLayoutEffect(() => {
    if (!highlightExtractCta && !highlightMethodCta) return undefined;
    const el = highlightExtractCta
      ? guideExtractCtaRef.current
      : guideMethodCtaRef.current;
    if (!el) return undefined;
    let inner = 0;
    const outer = window.requestAnimationFrame(() => {
      inner = window.requestAnimationFrame(() => {
        revealGuideAttentionTarget(el);
      });
    });
    return () => {
      window.cancelAnimationFrame(outer);
      if (inner) window.cancelAnimationFrame(inner);
    };
  }, [
    description,
    embedFlowStep,
    highlightExtractCta,
    highlightMethodCta,
    sourceHint,
  ]);

  /** Guided demo: do not walk back before the chapter's wizard step. */
  const canWizardBackTo = useCallback(
    (targetStep) => {
      if (!guideMinWizardStep) return true;
      const targetIdx = wizardStepIndex(targetStep);
      const minIdx = wizardStepIndex(guideMinWizardStep);
      if (targetIdx < 0 || minIdx < 0) return true;
      return targetIdx >= minIdx;
    },
    [guideMinWizardStep]
  );

  // After refresh: restore entity, wizard step, and CPH notes from guide state.
  // Do not early-return when the anchor already matches — notes/step can still
  // be missing if a stale session prefill booted on anchor/pick.
  useEffect(() => {
    if (!productGuide?.active) return;
    const guideState = productGuide.state;
    if (!guideState?.scriptId) return;
    const prefill = buildGuideWizardPrefill(guideState);
    if (!prefill) return;

    if (prefill.id) {
      if (String(knowledgeAnchor?.id || "").trim() !== String(prefill.id)) {
        setKnowledgeScopeType("entity");
        setKnowledgeAnchor({
          id: prefill.id,
          name: prefill.name || "",
          scopeType: "entity",
          extractMode: "auto",
        });
        setGuideStartCreate(false);
        setGuideCreateName("");
      }
    }

    const step = prefill.startStep || "anchor";
    const behind =
      wizardStepIndex(embedFlowStep) >= 0 &&
      wizardStepIndex(step) >= 0 &&
      wizardStepIndex(embedFlowStep) < wizardStepIndex(step);
    if (behind || isWizardStepBeforeGuide(embedFlowStep, guideState.chapterId)) {
      setEmbedFlowStep(step);
    }

    if (prefill.pasteText) {
      setPendingGuidePaste((prev) =>
        String(prev || "").trim() ? prev : prefill.pasteText
      );
      const wantSource =
        step === "source" ||
        embedFlowStep === "source" ||
        guideState.chapterId === "supply" ||
        Boolean(guideState.sampleNotesLoaded);
      if (wantSource) {
        setDescription((prev) =>
          String(prev || "").trim() ? prev : prefill.pasteText
        );
        if (prefill.nextStepHint) setSourceHint(prefill.nextStepHint);
        else if (guideState.sampleNotesLoaded) setSourceHint("extract");
      }
    }
  }, [
    embedFlowStep,
    knowledgeAnchor?.id,
    productGuide?.active,
    productGuide?.state,
    productGuide?.state?.entityId,
    productGuide?.state?.chapterId,
    productGuide?.state?.wizardStep,
    productGuide?.state?.sampleNotesLoaded,
  ]);

  // Snap wizard forward if the user (or a remount) landed before the guide step.
  useEffect(() => {
    if (!guideWizardLock || !guideMinWizardStep) return;
    if (!isWizardStepBeforeGuide(embedFlowStep, guideChapterId)) return;

    const want = guideMinWizardStep;
    if (want === "anchor" || want === "pick" || want === "source") {
      if (!knowledgeScopeType) setKnowledgeScopeType("entity");
    }
    if (want === "anchor" && !knowledgeAnchor) {
      setGuideStartCreate(true);
      const sample =
        productGuide?.script?.sampleName || "Customer Payments Hub";
      setGuideCreateName((prev) => String(prev || sample).trim() || sample);
    }
    // Cannot show pick/source without an anchor — hold on anchor instead.
    if ((want === "pick" || want === "source") && !knowledgeAnchor) {
      setGuideStartCreate(true);
      setEmbedFlowStep("anchor");
      return;
    }
    setEmbedFlowStep(want);
    if (want === "source") {
      const paste =
        String(pendingGuidePaste || "").trim() ||
        String(productGuide?.script?.sampleNotes || "").trim();
      if (paste) {
        setPendingGuidePaste(paste);
        setDescription((prev) => (String(prev || "").trim() ? prev : paste));
        setSourceHint((prev) => prev || "extract");
      }
    }
  }, [
    embedFlowStep,
    guideChapterId,
    guideMinWizardStep,
    guideWizardLock,
    knowledgeAnchor,
    knowledgeScopeType,
    pendingGuidePaste,
    productGuide?.script?.sampleName,
    productGuide?.script?.sampleNotes,
  ]);

  /** v2 embedded: drop banner copy (e.g. “Example description loaded…”) when leaving the step or changing source type. */
  useEffect(() => {
    if (wizardStep !== "input") return;
    setMessage("");
  }, [wizardStep, embedFlowStep, activeTab]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!description.trim()) {
      setMessage("Please enter a description for the IT System.");
      return;
    }

    // Process description directly without refinement
    await processDescriptionDirectly(description);
  };

  const processDescriptionDirectly = async (finalDescription) => {
    const anchorId = String(knowledgeAnchor?.id || "").trim();
    if (!anchorId) {
      setMessage("Select where this knowledge belongs before submitting.");
      setEmbedFlowStep(knowledgeScopeType ? "anchor" : "scope");
      return;
    }
    let provisionalId = null;
    try {
      if (isLlmQuotaExhausted()) {
        setMessage(`❌ ${LLM_QUOTA_EXHAUSTED_MESSAGE}`);
        setWizardStep("error");
        return;
      }

      setLoading(true);
      setIsProcessing(true);
      setMessage(addKnowledgeQueueLabel(knowledgeAnchor));

      const anchorLabel =
        knowledgeAnchor?.name ||
        "Knowledge";
      provisionalId = beginOptimisticEnrichmentQueue({
        anchorName: anchorLabel,
        anchorApplicationId: anchorId,
      });
      setAiProcessing(true);

      const response = await queueAddKnowledgeJob(api, knowledgeAnchor, [
        {
          kind: "snippet",
          label: "Add knowledge — pasted text",
          text: finalDescription,
        },
      ]);

      const optimisticJobId = finalizeOptimisticEnrichmentQueue(provisionalId, response, {
        anchorName: anchorLabel || response.matched_application_name,
        anchorApplicationId: anchorId,
      });
      debugLog("✅ Stored optimistic knowledge job:", optimisticJobId);

      if (response.job_id) {
        setMessage(`✅ ${addKnowledgeSuccessMessage(knowledgeAnchor, response)}`);
        setWizardStep("queued");
        emitGuideEvent("job-queued", {
          jobId: String(response.job_id),
          entityId: anchorId,
          entityName: anchorLabel || "",
        });

        if (onEntitiesProcessed) {
          onEntitiesProcessed(response.entities || []);
        }

        setAiProcessing(true);
      } else {
        setMessage(
          `❌ Could not queue job: ${response.error || response.message || "Unknown error"}`
        );
        setWizardStep("error");
        rollbackOptimisticEnrichmentQueue(provisionalId, optimisticJobId);
      }

      setLoading(false);
      setIsProcessing(false);
    } catch (error) {
      debugError("Error processing description:", error);
      setLoading(false);
      setIsProcessing(false);

      const rawDetail = error?.response?.data?.detail;
      let apiDetail = "";
      if (typeof rawDetail === "string") {
        apiDetail = rawDetail;
      } else if (Array.isArray(rawDetail)) {
        apiDetail = rawDetail
          .map((x) => (typeof x === "string" ? x : x?.msg || JSON.stringify(x)))
          .filter(Boolean)
          .join(" ");
      }

      let errorMessage = "Failed to queue knowledge ingestion. ";
      if (apiDetail) {
        errorMessage += apiDetail;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += "Please try again.";
      }

      if (isEnrichmentQueueTimeoutError(error)) {
        finalizeOptimisticEnrichmentQueueOnTimeout(provisionalId, {
          anchorName: knowledgeAnchor?.name || "Knowledge",
          anchorApplicationId: anchorId,
        });
        setMessage(
          "✅ Your request may still be queued. Check job status or Insights Inbox if extraction was enabled."
        );
        setWizardStep("queued");
        setAiProcessing(true);
        return;
      }

      rollbackOptimisticEnrichmentQueue(provisionalId);
      setMessage(`❌ ${errorMessage}`);
    }
  };

  const handleCreateNew = () => {
    setDescription("");
    setMessage("");
    setLoading(false);
    setIsProcessing(false);
  };

  // Handle batch processing started
  const handleBatchProcessingStarted = (detail = {}) => {
    debugLog("🔄 Batch processing started - setting wizard step to queued");
    debugLog("🔄 Current wizard step before setting:", wizardStep);
    ensureEnrichmentQueueVisibleFromUpload({
      anchorName: knowledgeAnchor?.name || "Application",
      anchorApplicationId: knowledgeAnchor?.id,
      jobId: detail?.jobId || detail?.job_id,
    });
    setWizardStep("queued");
    setMessage(`✅ ${addKnowledgeSuccessMessage(knowledgeAnchor, detail)}`);
    const queuedId = String(detail?.jobId || detail?.job_id || "").trim();
    if (queuedId) {
      emitGuideEvent("job-queued", {
        jobId: queuedId,
        entityId: String(knowledgeAnchor?.id || ""),
        entityName: String(knowledgeAnchor?.name || ""),
      });
    }

    // Verify the step was set
    setTimeout(() => {
      debugLog("🔄 Wizard step after setting (100ms later):", wizardStep);
    }, 100);

    // Don't auto-reset - let the user manually reset or let it reset when they navigate away
    // The wizard step will remain 'queued' until explicitly reset
    debugLog(
      "🔄 Wizard step set to queued - will remain until manually reset"
    );
  };

  // File uploads queue enrichment in MultiFileUpload; no legacy follow-up extraction.
  const handleFilesProcessed = (files) => {
    debugLog("📁 Enrichment upload finished:", files?.length ?? 0);
    setUploadedFiles(files || []);
  };

  // Handle file processing errors
  const handleFileError = (error) => {
    debugError("📁 File processing error:", error);
    setFileProcessingError(error);
  };

  // Wizard navigation functions
  const resetWizard = () => {
    setWizardStep("input");
    setEmbedFlowStep("scope");
    setKnowledgeScopeType(null);
    setKnowledgeAnchor(null);
    setMessage("");
    setLoading(false);
    setIsProcessing(false);
  };

  const shellClass = "w-full py-0";
  const inputPanelClass = isMobile
    ? "rounded-2xl border border-slate-200/90 bg-white/90 shadow-sm ring-1 ring-slate-100/80 p-3 mb-0"
    : "rounded-2xl border border-slate-200/90 bg-white/90 shadow-sm ring-1 ring-slate-100/80 p-4 mb-0";
  const wizardStackClass = "space-y-3";
  const methodCardPad = isMobile ? "min-h-0 p-3.5" : "p-4";
  const methodIconClass = isMobile
    ? "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg"
    : "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg";

  return (
    <div className={shellClass}>
      <div className="w-full max-w-none">
        {/* Message Display - Prominent at the top */}
        {message && (
          <div className={systemInputMessageBannerClass(message)}>
            <div className="flex items-start space-x-3">
              <span
                className={`text-4xl mt-1 ${
                  message.includes("No meaningful") ||
                  message.includes("No entities")
                    ? "🔍"
                    : message.includes("Error") || message.includes("❌")
                      ? "❌"
                      : message.includes("Success") ||
                          message.includes("✅") ||
                          message.includes("successfully")
                        ? "✅"
                        : "ℹ️"
                }`}
              ></span>
              <div className="flex-1">
                <h3
                  className={`font-bold text-lg mb-2 ${
                    message.includes("No meaningful") ||
                    message.includes("No entities")
                      ? "Missing Potential Entities"
                      : message.includes("Error") || message.includes("❌")
                        ? "Processing Error"
                        : message.includes("Success") ||
                            message.includes("✅") ||
                            message.includes("successfully")
                          ? "Success!"
                          : "Information"
                  }`}
                >
                  {message.includes("No meaningful") ||
                  message.includes("No entities")
                    ? "Missing Potential Entities"
                    : message.includes("Error") || message.includes("❌")
                      ? "Processing Error"
                      : message.includes("Success") ||
                          message.includes("✅") ||
                          message.includes("successfully")
                        ? "Success!"
                        : "Information"}
                </h3>
                <div
                  className={`text-sm leading-relaxed whitespace-pre-line${
                    loading ? " blink-soft" : ""
                  }`}
                >
                  {message}
                </div>

                {/* Action Buttons for Errors and Missing Entities */}
                {!loading &&
                  (message.includes("Error") ||
                    message.includes("❌") ||
                    message.includes("No meaningful") ||
                    message.includes("No entities")) && (
                    <div
                      className={`mt-4 pt-3 border-t ${
                        message.includes("No meaningful") ||
                        message.includes("No entities")
                          ? "border-orange-400/30"
                          : "border-red-400/30"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-xs opacity-90">
                          {message.includes("timeout")
                            ? "🕐 Processing took too long"
                            : message.includes("Network")
                              ? "🌐 Network connection issue"
                              : message.includes("No meaningful")
                                ? "📝 Description needs more detail"
                                : "❌ Processing failed"}
                        </div>
                        <button
                          onClick={() =>
                            handleSubmit({ preventDefault: () => {} })
                          }
                          className={`px-4 py-2 text-white text-sm font-medium rounded transition-colors ${
                            message.includes("No meaningful") ||
                            message.includes("No entities")
                              ? "bg-orange-500 hover:bg-orange-600"
                              : "bg-red-500 hover:bg-red-600"
                          }`}
                        >
                          🔄 Try Again
                        </button>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}

        {/* Wizard Content */}
        {wizardStep === "input" && (
          <div className={wizardStackClass}>
            {embedFlowStep === "scope" && (
              <AddKnowledgeScopePanel
                onSelect={(nextScope) => {
                  if (nextScope === "content") {
                    setKnowledgeScopeType("content");
                    setKnowledgeAnchor({
                      id: "content",
                      name: "New — identified from your content",
                      scopeType: "content",
                      extractMode: "force_on",
                    });
                    setEmbedFlowStep("pick");
                    return;
                  }
                  if (nextScope !== knowledgeScopeType) {
                    setKnowledgeAnchor(null);
                  }
                  setKnowledgeScopeType(nextScope);
                  setEmbedFlowStep("anchor");
                }}
              />
            )}

            {embedFlowStep === "anchor" && knowledgeScopeType && (
              <AddKnowledgeAnchorPanel
                embedded
                scopeType={knowledgeScopeType}
                anchor={knowledgeAnchor}
                onAnchorChange={handleKnowledgeAnchorChange}
                initialCreateName={guideCreateName}
                startInCreateMode={guideStartCreate}
                highlightCreate={Boolean(
                  guideWizardLock &&
                    !knowledgeAnchor?.id &&
                    !productGuide?.state?.entityId
                )}
                highlightContinue={Boolean(
                  guideWizardLock &&
                    (knowledgeAnchor?.id || productGuide?.state?.entityId)
                )}
                onContinue={() => {
                  setEmbedFlowStep("pick");
                  emitGuideEvent("anchor-continued", {
                    entityId: String(knowledgeAnchor?.id || ""),
                    entityName: String(knowledgeAnchor?.name || ""),
                  });
                }}
                onBack={
                  canWizardBackTo("scope")
                    ? () => {
                        setKnowledgeAnchor(null);
                        setEmbedFlowStep("scope");
                      }
                    : null
                }
              />
            )}

            {embedFlowStep === "pick" && knowledgeAnchor && (
            <div className={isMobile ? "mb-1" : "mb-2"}>
              {/* One-line header: back, step, question and target inline. */}
              <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-left">
                {canWizardBackTo(
                  knowledgeScopeType === "content" ? "scope" : "anchor"
                ) ? (
                <AddKnowledgeWizardBackButton
                  onClick={() =>
                    // "New" target has no anchor step — go straight back to Target.
                    setEmbedFlowStep(
                      knowledgeScopeType === "content" ? "scope" : "anchor"
                    )
                  }
                >
                  Change target
                </AddKnowledgeWizardBackButton>
                ) : null}
                <p className="text-sm font-semibold tracking-tight text-slate-900">
                  Step {knowledgeScopeType === "content" ? 2 : 3} · How do you want to add it?
                </p>
              </div>
              <div
                className={
                  isMobile
                    ? "grid grid-cols-1 gap-2.5"
                    : "grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4"
                }
                role="group"
                aria-label="Knowledge source type"
              >
                {(isMobile
                  ? ADD_KNOWLEDGE_METHODS.filter((m) => m.id !== "s3")
                  : ADD_KNOWLEDGE_METHODS
                ).map((m) => {
                  const st = m.embed;
                  const isGuideMethod =
                    highlightMethodCta && m.id === "text";
                  return (
                      <button
                        key={m.id}
                        type="button"
                        ref={isGuideMethod ? guideMethodCtaRef : undefined}
                        onClick={() => {
                          setActiveTab(m.id);
                          if (m.id === "text" && pendingGuidePaste) {
                            setDescription(pendingGuidePaste);
                            setPendingGuidePaste("");
                            setMessage(
                              "Sample notes ready — edit if you like, then Add knowledge."
                            );
                          }
                          setEmbedFlowStep("source");
                          emitGuideEvent("method-picked", { method: m.id });
                        }}
                        className={`group relative flex w-full flex-col rounded-2xl border ${st.border} ${st.gradient} ${methodCardPad} text-left shadow-sm transition duration-200 ease-out ${st.ringHover} hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2${
                          isGuideMethod ? " example-cta-attention" : ""
                        }`}
                      >
                        {m.badge ? (
                          <span
                            className={`absolute rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800 ring-1 ring-emerald-500/20 ${
                              isMobile ? "right-3 top-3" : "right-4 top-4"
                            }`}
                          >
                            {m.badge}
                          </span>
                        ) : null}
                        <div className="flex items-start gap-3">
                          <div
                            className={`${methodIconClass} ${st.iconShell}`}
                            aria-hidden
                          >
                            {m.emoji}
                          </div>
                          <div className="min-w-0 flex-1 pt-0.5">
                            <span className="text-sm font-semibold tracking-tight text-slate-900">
                              {m.title}
                            </span>
                            <p className="mt-1 text-xs leading-relaxed text-slate-600">
                              {m.hint}
                            </p>
                          </div>
                        </div>
                        <div
                          className={`mt-auto flex items-center justify-end gap-1.5 text-xs font-medium text-blue-700 opacity-90 transition group-hover:opacity-100 ${
                            isMobile ? "pt-3" : "pt-2.5"
                          }`}
                        >
                          <span>Continue</span>
                          <svg
                            className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </button>
                    );
                })}
              </div>
            </div>
            )}

            {embedFlowStep === "source" && knowledgeAnchor && (
              <>
              {/* One toolbar row: back link + target chip share a line.
                  "See Example" moved down into the text panel header. */}
              <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                {canWizardBackTo("pick") ? (
                <AddKnowledgeWizardBackButton onClick={() => setEmbedFlowStep("pick")}>
                  Change how
                </AddKnowledgeWizardBackButton>
                ) : null}
                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs text-emerald-900">
                  <span className="font-medium">
                    {knowledgeAnchor.scopeType === "knowledge_surface"
                      ? "Category:"
                      : knowledgeAnchor.scopeType === "content"
                        ? "Target:"
                        : "For:"}
                  </span>
                  &nbsp;{knowledgeAnchor.name}
                </span>
              </div>
              {knowledgeAnchor.scopeType === "entity" ? (
                <ExtractModeOption
                  className="mb-3"
                  value={knowledgeAnchor.extractMode || "auto"}
                  onChange={handleExtractModeChange}
                />
              ) : null}

            {/* File Upload Section */}
            {activeTab === "file" && (
              <div className={inputPanelClass}>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    Step {knowledgeScopeType === "content" ? 3 : 4} — Upload files
                  </p>
                <MultiFileUpload
                  onFilesProcessed={handleFilesProcessed}
                  onError={handleFileError}
                  disabled={loading}
                  onNavigateToInbox={onNavigateToInbox}
                  onBatchProcessingStarted={handleBatchProcessingStarted}
                  trialStatus={trialStatus}
                  knowledgeAnchor={knowledgeAnchor}
                  anchorApplicationId={knowledgeAnchor?.id}
                  anchorApplicationName={knowledgeAnchor?.name}
                />

                {/* File Processing Error */}
                {fileProcessingError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p
                      className={
                        "text-red-800 text-sm"
                      }
                    >
                      {fileProcessingError}
                    </p>
                  </div>
                )}

                {/* Uploaded Files Summary */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <h4 className="text-sm font-medium text-slate-700 mb-2">
                      Uploaded Files ({uploadedFiles.length})
                    </h4>
                    <div className="space-y-1">
                      {uploadedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between text-xs"
                        >
                          <span
                            className={`${file.error ? "text-red-400" : "text-green-400"}`}
                          >
                            {file.error ? "❌" : "✅"} {file.filename}
                          </span>
                          {file.error && (
                            <span className="text-red-400">{file.error}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* S3 Import Section (desktop only — hidden on mobile) */}
            {activeTab === "s3" && !isMobile ? (
              <div className={inputPanelClass}>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  Step {knowledgeScopeType === "content" ? 3 : 4} — Import from S3
                </p>
                <S3ConnectorImport
                  onBatchProcessingStarted={handleBatchProcessingStarted}
                  onError={handleFileError}
                  onClearError={() => setFileProcessingError("")}
                  trialStatus={trialStatus}
                  knowledgeAnchor={knowledgeAnchor}
                  anchorApplicationId={knowledgeAnchor?.id}
                />
                {fileProcessingError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">{fileProcessingError}</p>
                  </div>
                )}
              </div>
            ) : null}

            {/* Text Input Section */}
            {activeTab === "text" && (
              <div className={inputPanelClass}>
                <form onSubmit={handleSubmit}>
                  <div className={isMobile ? "mb-3" : "mb-3"}>
                    {/* Panel header: step + field label on one line, with
                        See Example right beside the textarea it fills. */}
                    <div className="mb-2 flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Step {knowledgeScopeType === "content" ? 3 : 4} — Paste or type
                      </span>
                      <label
                        className={`font-medium ${isMobile ? "text-sm" : "text-base"} text-slate-800`}
                      >
                        {knowledgeAnchor?.scopeType === "content"
                          ? "Describe the new system"
                          : knowledgeAnchor?.name
                            ? `Knowledge about ${knowledgeAnchor.name}`
                            : "Describe Your IT System Landscape"}
                        <span className="text-red-400">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const testDescription =
                            "The Finance organization owns the Customer Order Management initiative, which aims to improve the Order Processing business capability. The Customer Portal is a web application built with React and Node.js that supports this capability. It handles the Customer Order Processing business process and manages Customer Data and Order Data. The application integrates with the Payment Gateway API service (provided by Stripe) and connects to a PostgreSQL database. The Finance organization uses this application, which is deployed on the AWS cloud platform. The initiative supports the objective of increasing sales revenue by 20% and uses technology components including Java 17 and Windows Server 2022.";
                          setDescription(testDescription);
                          if (sourceHint === "example") {
                            // Guided flow: the submit-row cue takes over
                            // ("click Extract/Add knowledge next") instead
                            // of the large banner.
                            setSourceHint("extract");
                            setMessage("");
                          } else {
                            setMessage(
                              knowledgeAnchor?.extractMode === "force_on"
                                ? 'Example description loaded. Click "Extract knowledge" to queue extraction.'
                                : knowledgeAnchor?.extractMode === "force_off"
                                  ? 'Example description loaded. Click "Index knowledge" to add to search.'
                                  : 'Example description loaded. Click "Add knowledge" to queue ingestion.'
                            );
                          }
                        }}
                        className={`ml-auto inline-flex shrink-0 items-center rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-900 transition hover:bg-emerald-100/90 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 ${
                          sourceHint === "example" && !description.trim()
                            ? "example-cta-attention"
                            : ""
                        }`}
                        title="Load a sample description to test AI processing"
                      >
                        ✨ See Example
                      </button>
                    </div>
                    {sourceHint === "example" && !description.trim() ? (
                      <p className="mb-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                        For inspiration, click <strong>See example</strong> —
                        then edit it or replace it with your own.
                      </p>
                    ) : null}
                    <TextareaWithAutocomplete
                      value={description}
                      onChange={setDescription}
                      onSelect={(entity) => {
                        debugLog("Selected entity:", entity.name);
                      }}
                      placeholder={
                        knowledgeAnchor?.scopeType === "content"
                          ? "Notes about the new system — what it does, who owns it, what it connects to."
                          : knowledgeAnchor?.name
                            ? `Paste or type documentation, notes, or architecture detail about ${knowledgeAnchor.name} — integrations, data, capabilities, ownership, etc.`
                            : `Start describing your IT system here. For example:

"The Finance organization uses the Customer Portal application to support Order Processing. The application handles Customer Order Processing and manages Customer Data. It integrates with the Payment Gateway API (provided by Stripe) and uses PostgreSQL database on AWS platform."`
                      }
                      className="w-full"
                      rows={isMobile ? 7 : 8}
                      minChars={1}
                      maxResults={10}
                      lightTheme
                    />
                  </div>

                  <div
                    ref={guideExtractCtaRef}
                    className="border-t border-slate-200 mt-3 pt-3"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                      {sourceHint === "extract" && description.trim() ? (
                        <p className="text-xs font-medium text-emerald-800 sm:mr-auto">
                          Example loaded — click{" "}
                          <strong>
                            {knowledgeAnchor?.extractMode === "force_on"
                              ? "Extract knowledge"
                              : knowledgeAnchor?.extractMode === "force_off"
                                ? "Index knowledge"
                                : "Add knowledge"}
                          </strong>{" "}
                          next →
                        </p>
                      ) : null}
                      <LoadingButton
                        type="submit"
                        loading={loading}
                        loadingLabel="Processing..."
                        disabled={
                          loading ||
                          !description.trim() ||
                          isLlmQuotaExhausted()
                        }
                        className={`w-full sm:w-auto sm:min-w-[220px] rounded-xl bg-blue-600 text-white font-semibold shadow-md shadow-blue-600/20 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-colors ${
                          isMobile
                            ? "px-5 py-2.5 text-sm"
                            : "px-6 py-3 text-base"
                        } ${
                          highlightExtractCta ? "extract-cta-attention" : ""
                        }`}
                      >
                        {knowledgeAnchor?.extractMode === "force_on"
                          ? "Extract knowledge"
                          : knowledgeAnchor?.extractMode === "force_off"
                            ? "Index knowledge"
                            : "Add knowledge"}
                      </LoadingButton>
                    </div>
                  </div>
                </form>
              </div>
            )}
              </>
            )}

          </div>
        )}

        {/* Queued Processing Step */}
        {wizardStep === "queued" && (
            <div
              className={`rounded-xl border border-emerald-200 bg-emerald-50/90 text-center space-y-3 ${
                isMobile ? "p-3" : "p-4"
              }`}
            >
              <p className="text-sm font-semibold text-emerald-900">Queued</p>
              <p className="text-xs text-emerald-800/90">
                Extraction runs in the background.
              </p>
              <details className="text-left rounded-lg border border-emerald-100 bg-white/70 px-3 py-2 text-xs text-emerald-900/85">
                <summary className="cursor-pointer font-medium text-emerald-950">
                  What to expect
                </summary>
                <p className="mt-2 leading-relaxed">
                  Often a few minutes for small inputs; longer for large text or
                  batches. Follow progress in{" "}
                  <strong>Insights Inbox</strong> when you’re ready to review.
                </p>
              </details>
              <div className="flex flex-col-reverse sm:flex-row justify-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setWizardStep("input");
                    setDescription("");
                    setKnowledgeScopeType(null);
                    setKnowledgeAnchor(null);
                    setEmbedFlowStep("scope");
                  }}
                  className="px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Add another
                </button>
                {onNavigateToInbox && (
                  <button
                    type="button"
                    onClick={() => onNavigateToInbox()}
                    className="blink-soft-slow px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                  >
                    Open Insights Inbox
                  </button>
                )}
              </div>
            </div>
          )}

        {/* Processing Step - Show when actively processing */}
        {wizardStep === "processing" && (
            <div
              className={`rounded-xl border border-amber-200 bg-amber-50/90 text-center space-y-3 ${
                isMobile ? "p-3" : "p-4"
              }`}
            >
              <div className="flex justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
              </div>
              <p className="text-sm font-medium text-amber-950">Working…</p>
              <details className="text-left rounded-lg border border-amber-100 bg-white/70 px-3 py-2 text-xs text-amber-900/85 max-w-md mx-auto">
                <summary className="cursor-pointer font-medium text-amber-950">
                  Details
                </summary>
                <p className="mt-2 leading-relaxed">
                  The model is reading your input and extracting entities and
                  relationships. Large pastes or files can take several minutes.
                </p>
              </details>
            </div>
          )}

        {/* Success Step - Show when processing completes */}
        {wizardStep === "success" && (
            <div
              className={`rounded-xl border border-emerald-200 bg-emerald-50/90 text-center space-y-3 ${
                isMobile ? "p-3" : "p-4"
              }`}
            >
              <p className="text-sm font-semibold text-emerald-900">Done</p>
              <p className="text-xs text-emerald-800/90">
                New items are ready to review.
              </p>
              <details className="text-left rounded-lg border border-emerald-100 bg-white/70 px-3 py-2 text-xs text-emerald-900/85">
                <summary className="cursor-pointer font-medium text-emerald-950">
                  Learn more
                </summary>
                <p className="mt-2 leading-relaxed">
                  Open <strong>Insights Inbox</strong> to approve or adjust extracted
                  entities before they fully merge into your catalog.
                </p>
              </details>
              <div className="flex flex-col-reverse sm:flex-row justify-center gap-2">
                <button
                  type="button"
                  onClick={resetWizard}
                  className="px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Add another
                </button>
                {onNavigateToInbox && (
                  <button
                    type="button"
                    onClick={() => onNavigateToInbox()}
                    className="blink-soft-slow px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                  >
                    Open Insights Inbox
                  </button>
                )}
              </div>
            </div>
          )}

      </div>
    </div>
  );
};

export default SystemInput;
